import { useMutation } from '@tanstack/react-query';

import { queryClient } from '~app/query-client';

import { baseAPI } from '~shared/api/base-api';
import { KEYS } from '~shared/api/query-keys';

type Days = 'Sun' | 'Mon' | 'Tue' | 'Wed' | 'Thu' | 'Fri' | 'Sat';
export interface ClassDetailsForm {
  // General Information
  title: string;
  description: string; // 'An introductory piano class for beginners.';
  location: string | null;
  room: string | null;
  studio?: string;
  instructor: Array<string>;
  tags: Array<string>;
  group: Array<string>;
  hide: boolean;
  color: string;

  // Schedule
  startDate: string; // '2024-11-01T00:00:00Z';
  endDate: string; // '2025-05-31T00:00:00Z';
  startTime: string; // 2024-11-01T10:00:00Z
  endTime?: string; // 2024-11-01T10:00:00Z
  days: Array<Days>;
  duration: number; // '1 hour';
  isPrivateLesson?: boolean;
  slotInterval?: number;
  session: string;

  // Enrollment
  startYear: number; // 5;
  startMonth: number; // 5;
  endYear: number; // 12;
  endMonth: number; // 12;
  maxSize: number; // 20;
  policyGroup: Array<string>; // 'Standard Policies';
  registrationStartDate: string; // '2024-10-31T00:00:00Z';
  registrationFeeAmount: number; // 50;
  prorateTuition: boolean;
  prorateOneTimePackage?: boolean;
  tuitionFee: number;
  billingDay?: number;
  costumeFee: number;
  tuitionBillingCycle: string; // 'Monthly' | 'Weekly' | 'One Time' | 'every-x-days';
  billingFrequencyDays?: number;
  hourlyTuitionBillingId: string | null;
  classCountTuitionBillingId?: string | null;
  // Display Options
  displayOnWebsite?: boolean; // true;
  allowOnlineRegistration?: boolean; // true;
  allowPortalEnrollment?: boolean; // true;
  allowTrialEnrollment?: boolean; // false;
  includeInPublicSchedule?: boolean;
  acceptsMakeups?: boolean;
  availability: Array<{
    day: string;
    startTime: string;
    endTime: string;
  }>;
  timeConfig: string;
}

export const createClassAPI = async (
  file: File | string | undefined,
  fileType: 'file' | 'url' | undefined,
  classDetails: ClassDetailsForm,
) => {
  const formData = new FormData();

  if (file && fileType === 'file') {
    formData.append('file', file as File);
    formData.append('fileType', fileType);
  } else if (file && fileType === 'url') {
    formData.append('fileUrl', file as string);
    formData.append('fileType', fileType);
  }

  // Add stringified class details as payload
  formData.append('payload', JSON.stringify(classDetails));

  // Do NOT set Content-Type when sending FormData - let the client set it with boundary
  const resp = await baseAPI.post<unknown>('/enrollments', formData);
  return resp.data;
};

export const useCreateClassMutation = () => {
  return useMutation({
    mutationFn: async ({
      file,
      fileType,
      classDetails,
    }: {
      file?: File | string;
      fileType?: 'file' | 'url';
      classDetails: ClassDetailsForm;
    }) => {
      const response = await createClassAPI(file, fileType, classDetails);
      return response;
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({
        predicate(query) {
          return query.queryKey[0] === KEYS.CLASSES;
        },
        // The class lists are unmounted while Add Class is open, so the default
        // 'active' refetch would leave them on their 5-minute stale snapshot
        // until a manual reload (ENR-3078).
        refetchType: 'all',
      });
    },
  });
};
